import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, adminApi } from '@wa/shared';
import { Cell, DataTable, Empty, Page, when } from '@/components/Primitives';

const LEVEL: Record<number, { label: string; tone: string }> = {
  2: { label: 'warning', tone: 'text-(--color-warn)' },
  3: { label: 'error', tone: 'text-(--color-bad)' },
  4: { label: 'fatal', tone: 'text-(--color-bad) font-semibold' },
};

export function ErrorsPage() {
  const [note, setNote] = useState<string | null>(null);

  const errors = useQuery({
    queryKey: ['admin', 'errors'],
    queryFn: () => adminApi.errors(150),
    refetchInterval: 15_000,
  });

  // A webhook event keeps its raw payload whatever happens to the processing, so
  // anything that failed for a reason since fixed can simply be run again.
  const retry = useMutation({
    mutationFn: adminApi.retryFailedWebhooks,
    onSuccess: (result) =>
      setNote(result.requeued === 0
        ? 'No failed webhooks to retry.'
        : `${result.requeued} webhook event(s) back on the queue.`),
    onError: (e) => setNote(e instanceof ApiError ? e.message : 'Could not requeue.'),
  });

  return (
    <Page
      title="Errors"
      subtitle="Warning and above, from every process. The correlation id ties a row here to the request that caused it."
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => retry.mutate()}
          disabled={retry.isPending}
          className="tap rounded-lg border border-(--color-line) px-3 py-2 text-body text-(--color-muted) hover:text-(--color-ink) disabled:opacity-40"
        >
          {retry.isPending ? 'Requeueing…' : 'Retry failed webhooks'}
        </button>

        {note && <span className="text-small text-(--color-muted)">{note}</span>}
      </div>

      <DataTable head={['When', 'Level', 'Source', 'Category', 'Tenant', 'Message', 'Correlation']}>
        {errors.data?.map((e) => {
          const level = LEVEL[e.level] ?? { label: String(e.level), tone: '' };
          return (
            <tr key={e.id}>
              <Cell nowrap>{when(e.occurredAt)}</Cell>
              <Cell nowrap><span className={level.tone}>{level.label}</span></Cell>
              <Cell mono nowrap>{e.source}</Cell>
              <Cell mono nowrap>{e.category}</Cell>
              <Cell nowrap>{e.tenantName ?? '—'}</Cell>
              <Cell>
                <span className="block max-w-xl">{e.message}</span>
                {e.requestPath && (
                  <span className="mt-0.5 block font-mono text-small text-(--color-muted)">{e.requestPath}</span>
                )}
              </Cell>
              <Cell mono nowrap>{e.correlationId?.slice(0, 8) ?? '—'}</Cell>
            </tr>
          );
        })}
      </DataTable>
      {errors.data?.length === 0 && <Empty>Nothing logged. That is the good outcome.</Empty>}
    </Page>
  );
}
